import { useState, useEffect, useCallback } from 'react';

// Custom hook to manage all NPC related state and logic
const useNPCData = () => {
  const [npcNames, setNpcNames] = useState([]);
  const [currentName, setCurrentName] = useState('');
  const [selectedNpc, setSelectedNpc] = useState('');
  const [dialogues, setDialogues] = useState({});
  const [currentDialogue, setCurrentDialogue] = useState('');
  const [currentButton, setCurrentButton] = useState('');
  const [buttons, setButtons] = useState({});
  const [selectedDialogue, setSelectedDialogue] = useState('');
  const [commands, setCommands] = useState({});
  const [editingDialogue, setEditingDialogue] = useState(null);
  const [collapsedNpcs, setCollapsedNpcs] = useState({});
  // Track if we need to synchronize buttons
  const [needsSync, setNeedsSync] = useState(false);

  // Helper function to find NPC name by tag
  const findNpcNameByTag = (tag) => {
    for (const name of npcNames) {
      const nameTag = name.toLowerCase().replace(/\s+/g, '_').replace(/\W/g, '');
      if (nameTag === tag) {
        return name;
      }
    }
    return null;
  };

  // Function to get dialogues that are pointed to by buttons
  const getPointedDialogues = () => {
    const pointedDialogues = {};
    
    // Go through all commands to find dialogue open commands
    Object.entries(commands).forEach(([npc, dialogueCommands]) => {
      Object.entries(dialogueCommands).forEach(([dialogue, buttonCommands]) => {
        Object.entries(buttonCommands).forEach(([button, cmdList]) => {
          cmdList.forEach(cmd => {
            // Look for dialogue open commands
            if (cmd.includes('/dialogue open')) {
              // Extract target NPC and dialogue index
              const match = cmd.match(/\/dialogue open @e\[tag=([^\]]+)\][^.]+\.(\d+)/);
              if (match) {
                const targetNpcTag = match[1];
                const targetDialogueIndex = parseInt(match[2]);
                
                // Find the actual NPC name from the tag
                for (const name of npcNames) {
                  const nameTag = name.toLowerCase().replace(/\s+/g, '_').replace(/\W/g, '');
                  if (nameTag === targetNpcTag) {
                    // Initialize if needed
                    if (!pointedDialogues[name]) {
                      pointedDialogues[name] = [];
                    }
                    // Add this dialogue index to the pointed list
                    pointedDialogues[name][targetDialogueIndex] = true;
                    break;
                  }
                }
              }
            }
          });
        });
      });
    });
    
    return pointedDialogues;
  };

  // Function to synchronize button data (wrapped in useCallback to avoid dependency warnings)
  const synchronizeButtonData = useCallback(() => {
    try {
      const buttonTranslations = JSON.parse(localStorage.getItem('buttonTranslations') || '{}');
      
      // Skip if no translations available
      if (Object.keys(buttonTranslations).length === 0) return;
      
      // Go through all buttons and try to match with translations
      setButtons(prevButtons => {
        const updatedButtons = {...prevButtons};
        let hasChanges = false;
        
        Object.entries(updatedButtons).forEach(([npc, dialogues]) => {
          Object.entries(dialogues).forEach(([dialogue, buttonArray]) => {
            const updatedButtonArray = buttonArray.map(button => {
              // Check if this button text is actually a translation key
              if (button.startsWith('dialogue.button.') || button === '$placeholder_button') {
                // Try to find a translation
                return buttonTranslations[button] || button;
              }
              
              // Try to find a matching translation key
              for (const [, translation] of Object.entries(buttonTranslations)) {
                if (translation === button) {
                  return button; // Already has the translated value
                }
              }
              
              return button;
            });
            
            // Only update if there's a change to avoid infinite loops
            if (JSON.stringify(updatedButtonArray) !== JSON.stringify(buttonArray)) {
              updatedButtons[npc][dialogue] = updatedButtonArray;
              hasChanges = true;
            }
          });
        });
        
        // Only return new object if changes were made
        return hasChanges ? updatedButtons : prevButtons;
      });
      
      // Reset the sync flag
      setNeedsSync(false);
    } catch (error) {
      console.error("Error synchronizing button data:", error);
    }
  }, []);

  // Trigger sync when dialogues change
  useEffect(() => {
    setNeedsSync(true);
  }, [dialogues]);

  // Perform synchronization when needed
  useEffect(() => {
    if (needsSync && localStorage.getItem('buttonTranslations')) {
      synchronizeButtonData();
    }
  }, [needsSync, synchronizeButtonData]);

  return {
    // State
    npcNames,
    setNpcNames,
    currentName,
    setCurrentName,
    selectedNpc,
    setSelectedNpc,
    dialogues,
    setDialogues,
    currentDialogue,
    setCurrentDialogue,
    currentButton, 
    setCurrentButton,
    buttons,
    setButtons,
    selectedDialogue,
    setSelectedDialogue,
    commands,
    setCommands,
    editingDialogue,
    setEditingDialogue,
    collapsedNpcs,
    setCollapsedNpcs,
    
    // Helper functions
    findNpcNameByTag,
    getPointedDialogues,
    synchronizeButtonData
  };
};

export default useNPCData;